import { Navigate, useLocation } from "react-router-dom";
import { Badge, SimpleGrid, Stack, Title } from "@mantine/core";
import { useAuthStore } from "@/lib/auth";
import { AreaTile } from "@/components/area-tile";
import { SectionGuides } from "@/components/guides/section-guides";
import { useSettingsAreaStatus } from "@/hooks/use-settings-area-status";
import { legacySettingsRedirect, settingsAreaPath, visibleSettingsAreas } from "@/lib/settings-areas";

/**
 * Settings: a landing grid of areas, each its own page at `/settings/<area>`
 * (pages/settings-area.tsx) — the Office Guide's shape. Members see only the
 * areas they can use; company-admin areas are hidden from everyone else.
 */
export default function TenantSettingsPage() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const { statuses, approvalEnabled } = useSettingsAreaStatus(!!user?.is_tenant_admin);

  // Slack's install flow used to land here (`?installed=` / `?error=`); its result belongs on the Slack page.
  const legacy = legacySettingsRedirect(location.search);
  if (legacy) return <Navigate to={legacy} replace />;

  const areas = visibleSettingsAreas(user, { approvalEnabled });

  return (
    <Stack p="md" gap="md">
      <Title order={2}>Settings</Title>
      <SectionGuides area="company" />
      <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
        {areas.map((area) => {
          const status = statuses[area.key];
          return (
            <AreaTile
              key={area.key}
              to={settingsAreaPath(area.key)}
              icon={area.icon}
              title={area.title}
              description={area.summary}
              status={
                status && (
                  <Badge variant="light" color={status.color} size="sm">
                    {status.label}
                  </Badge>
                )
              }
            />
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}
